import { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { CustomAccordion, CustomAlert, CustomButton, CustomSwitch } from '../../../../shared/uiComponents';
import { BACKGROUND } from '../../../../shared/Helpers/colors';
import { ProgramsContent } from './SkillAreas/Components/SkillAccordionContent';
import { ProgramsHeader } from './SkillAreas/Components/SkillAccordionHeader';
import { AddSkillContent } from './SkillAreas/Components/AddSkillContent';
import { AdminTypes } from '../../../../components/Menu';
import UploadTargets from './Actions/uploadTargets';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { getSkillAreas } from '../../../../redux/State/clientSlice/skillAreaSlice';
import { Plus } from '../../../../shared/Assets/svgIcons';
import { PlusIcon } from '../../../../shared/Assets/svg';
import { useScreenWidth } from '../../../../shared/Helpers/hooks';

interface SkillAcquisitionProps {
	mobileHeaderButton?: boolean;
	mobileUploadButton?: boolean;
}

const SkillAcquisition = ({ mobileHeaderButton, mobileUploadButton }: SkillAcquisitionProps = {}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const role = useSelector((state) => state.account.role);
	const skillAreas = useSelector((state) => state.skillArea.skillAreas);
	const [includeArchived, setIncludeArchived] = useState<boolean>(false);
	const [addSkillAreaOpen, setAddSkillAreaOpen] = useState<boolean>(false);

	const isBT = role?.section.id === AdminTypes.bt;

	useEffect(() => {
		if (!clientId) return;
		dispatch(getSkillAreas({ clientId, includeArchived }));
	}, [clientId, includeArchived, dispatch]);

	const showArchivedSkillAreasHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setIncludeArchived(checked);
	};

	const onCloseAddSkillArea = () => setAddSkillAreaOpen(false);

	// If this is just rendering the mobile upload button
	if (mobileUploadButton) {
		return isBT ? null : <UploadTargets />;
	}

	// If this is just rendering the mobile header button
	if (mobileHeaderButton) {
		return isBT ? null : (
			<CustomButton
				iconOnly={<PlusIcon />}
				buttonType="primary"
				sizeType="sm"
				onClick={() => setAddSkillAreaOpen(true)}
			/>
		);
	}

	return (
		<>
			<div className="skillAcquisitionHeaderWrapper">
				<div className="skillAcquisitionHeader">
					{!isBT && !isMobile && (
						<CustomButton
							title="Add Skill"
							onClick={() => setAddSkillAreaOpen(true)}
							buttonType="primary"
							startIcon={<Plus color={BACKGROUND.PRIMARY[100]} />}
						/>
					)}
					<CustomSwitch
						label="Show Archived"
						checked={includeArchived}
						onChange={showArchivedSkillAreasHandler}
						backgroundType="base"
						fullWidth={isMobile}
					/>
				</div>
				{!isBT && !isMobile && <UploadTargets />}
			</div>
			<div className="skillAcquisitionContainer">
				{skillAreas?.map((item) => (
					<div className="skillAcquisitionAccordion" key={item.id}>
						<CustomAccordion
							HeaderContent={() => <ProgramsHeader item={item} />}
							Content={() => <ProgramsContent item={item} />}
							defaultExpanded={false}
							accordionClassName="clientInfoAccordion"
							accordionDetailsClassName="skillAcquisitionAccordionDetails"
						/>
					</div>
				))}
			</div>
			<CustomAlert
				open={addSkillAreaOpen}
				onClose={onCloseAddSkillArea}
				title="Add Skill"
				Content={({ onClose }) => <AddSkillContent onClose={onClose} />}
			/>
		</>
	);
};

export default SkillAcquisition;
